/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, Component, ViewChild } from '@angular/core';
import { FileUpload } from '@dm/ang';
import { type Polling, CookieService, ERROR_OK, I18nService, LazyloadService, PromptService, ToastService, UtilService } from '@dm/core';
import { AuthService, ToolService } from 'app/common/services';
import { PatchService } from 'app/common/services/patch.service';

import { BatchConfigService, RunningStatue } from './batch-config.service';

type statusBar = 'upload' | 'check' | 'executing' | 'executed';

@Component({
    selector: 'app-batch-config',
    templateUrl: './batch-config.component.html',
    styleUrls: ['./batch-config.component.scss'],
})
export class BatchConfigComponent implements OnInit, OnDestroy {
    @ViewChild('cliUploader') inputEL: FileUpload;

    // i18n文本存储对象
    i18n;

    // 显示哪个状态栏
    currentStatusBar: statusBar = 'upload';

    // 控制侧滑窗口显示隐藏的标识
    isBatchConfigShow = true;

    // 经与后台同事同步，文件上传限制大小为100KB。
    maxFileSize = 102400;

    // 支持上传的文件类型，如"image/*"
    acceptFileType = '.conf';

    // 支持上传的文件后缀名，如".xml,.html"
    allowFileSuffix = '.conf';

    // CLI执行按钮启用标识
    enableExcuteButton = false;

    enableContinueBtn = false;

    // 手动删除
    clearConfigButton = false;

    // 关闭按钮禁用标识
    _disableCloseBtn: boolean;

    // 关闭按钮灰化时说明预校验中或cli命令执行中，超时例外启用
    set disableCloseBtn(value: boolean) {
        this._disableCloseBtn = value;
        if(value && !this.authService.getTimeoutExcept()) {
            this.authService.enableTimeoutExcept();
        }
        if(!value && this.authService.getTimeoutExcept()) {
            this.authService.disableTimeoutExcept();
        }
    }

    get disableCloseBtn(): boolean {
        return this._disableCloseBtn;
    }

    // 失败状态提示文案
    statusFailedLabel: string;

    // CLI执行进度值
    cliProgress = 0;

    // 允许导入CLI模板标识
    enableImportFile = true;

    // CLI停止执行按钮启用标识
    enableStopButton = true;

    // 未导入CLI模板标识
    notImported = true;

    // 用于保存buildrun接口查回的校验结果信息
    errorLineArr = [];

    // 用于显示的CLI执行结果数组
    cliEchoArr = [];

    // CLI执行状态轮询
    executionStatusPolling: Polling;

    // 上传文件的URL
    uploadURL = this.batchConfigService.getCliUploadUrl();

    onSelectPatch = this.patchService.onSelectPatch.bind(this);

    isOem = this.toolService.isOem();

    fileResult: string | ArrayBuffer = '';

    isShowDetailCode = false;

    constructor(
        private batchConfigService: BatchConfigService,
        private cookieService: CookieService,
        public i18nService: I18nService,
        private promptService: PromptService,
        private utilService: UtilService,
        private toast: ToastService,
        private lazyLoadService: LazyloadService,
        private patchService: PatchService,
        private toolService: ToolService,
        private authService: AuthService,
    ) { }

    ngOnInit(): void {
        this.i18n = {
            title: this.i18nService.get('settings.buildRun_term_batchConfig_lab'),
            failed: this.i18nService.get('common.downloadFailed_label'),
            explanation: this.i18nService.get('settings.buildRun_task_create_explanation_lab'),
            deleteByHand: this.i18nService.get('settings.buildRun_task_create_deleteByHand_link'),
            deleteByHandTitle: this.i18nService.get('settings.buildRun_task_create_deleteByHand_title'),
            deleteByHandTip: this.i18nService.get('settings.buildRun_task_create_uploadAgain_link'),
            sysInfoAndScriptTemplate: this.i18nService.get('settings.buildRun_task_create_sysInfoAndScriptTemplate_link'),
            importAndExecute: this.i18nService.get('settings.buildRun_task_create_des_importAndExecute_lab'),
            importAndExecuteOem: this.i18nService.get('settings.buildRun_task_create_des_importAndExecute_oem_lab'),
            serviceConfigurationFile: this.i18nService.get('settings.buildRun_task_create_serviceConfigurationFile_lab', true),
            notImport: this.i18nService.get('settings.buildRun_task_create_info_notImport_lab'),
            noProfile: this.i18nService.get('settings.buildRun_task_create_deleteByHand_error_info'),
            delete: this.i18nService.get('settings.buildRun_task_create_deleteByHand_confirm_msg'),
            succImport: this.i18nService.get('settings.buildRun_task_create_info_succImport_lab'),
            result: this.i18nService.get('settings.buildRun_term_batchConfig_info_result_lab'),
            stop: this.i18nService.get('common.stop_button'),
            close: this.i18nService.get('common.close_button'),
            cancelBtn: this.i18nService.get('common.cancel_button'),
            execute: this.i18nService.get('common.execute_button'),
            executing: this.i18nService.get('common.executing_lab'),
            continue: this.i18nService.get('common.continue_button'),
            pause: this.i18nService.get('common.EnumDefine_RUNNING_STATUS_E_PAUSE'),
            otherUser: this.i18nService.get('settings.buildRun_task_create_info_otherUser_msg'),
            importError: this.i18nService.get('settings.buildRun_task_import_error_msg'),
            importTimedOut: this.i18nService.get('common.task_progressTimeout_tip'),
            importVerifying: this.i18nService.get('settings.buildRun_task_create_info_importVerifying_lab'),
            abnormalStatus: this.i18nService.get('settings.buildRun_task_getBackgroundTask_abnormal_tip'),
            commonError: this.i18nService.get('common.systemBusy_msg'),
            internalError: this.i18nService.get('common.systemError_internal_msg'),
            contactEngineers: this.i18nService.get('common.contactTechSupport_msg'),
            initCheckTitle: this.i18nService.get('settings.buildRun_task_create_info_otherUser_title'),
            fileCheckTitle: this.i18nService.get('settings.buildRun_task_create_info_succImport_checkFile_title'),
            preExecutionCheckTitle: this.i18nService.get('settings.buildRun_task_import_error_title'),
            executionCheckTitle: this.i18nService.get('settings.buildRun_task_check_runningStatus_title'),
            configFilePreviewTips: this.i18nService.get('settings.buildRun_task_create_configFilePreview_info'),
            previewBtn: this.i18nService.get('settings.buildRun_task_create_Preview_btn'),
        };
        this.disableCloseBtn = false;
        this.statusFailedLabel = this.i18n.failed;

        // 加载页面时需要调“查看批量配置执行进度”接口，根据后台返回的状态决定按钮启用禁用及页面展示
        this.updatingInitStatus();
    }

    ngOnDestroy(): void {
        if (this.executionStatusPolling) {
            this.batchConfigService.stopPolling(this.executionStatusPolling);
        }
    }

    /**
     * 显示侧滑窗口
     *
     * @memberof BatchConfigComponent
     */
    showSlideWindow(): void {
        this.isBatchConfigShow = true;
    }

    /**
     * 隐藏侧滑窗口
     *
     * @memberof BatchConfigComponent
     */
    hideSlideWindow(): void {
        this.isBatchConfigShow = false;
    }

    /**
     * 关闭侧滑窗口后做的事
     *
     * @memberof BatchConfigComponent
     */
    onHide(): void {
        // 关闭侧滑窗后销毁组件
        this.lazyLoadService.destroySelf(this);
    }

    /**
     * 下载CLI模板文件
     *
     * @memberof BatchConfigComponent
     */
    downloadFile(): void {
        this.batchConfigService.downloadFile().subscribe();
    }

    /**
     * 上传文件前的回调
     *
     * @param {*} event
     * @memberof BatchConfigComponent
     */
    onBeforeUpload(): void {
        // 隐藏执行完成图标及提示，显示原生的上传控件状态栏
        this.currentStatusBar = 'upload';
        // 清空CLI执行结果数组
        this.cliEchoArr = [];
        // 清空CLI校验结果信息
        this.errorLineArr = [];
        // 将导入状态置为未导入
        this.notImported = true;
        // 禁用执行按钮
        this.enableExcuteButton = false;
        // 禁用关闭按钮
        this.disableCloseBtn = true;
    }

    /**
     * 上传文件前，添加TOKEN
     *
     * @param {*} event
     * @memberof BatchConfigComponent
     */
    onBeforeSend(event): void {
        event.xhr.setRequestHeader('iBaseToken', this.cookieService.getToken());
        event.xhr.setRequestHeader('Accept-Language', this.i18nService.language === 'ja' ? 'en' : this.i18nService.language);
    }

    /**
     * 上传过程中回调函数
     *
     * @param {*} event
     * @memberof BatchConfigComponent
     */
    onUploading(event): void {
        if (100 === event.file.progress) {
            // 隐藏原生的上传控件状态栏，显示自定义的校验中状态栏
        }
    }

    /**
     * 上传成功时的处理
     *
     * @memberof BatchConfigComponent
     */
    handleUploadSuccess(): void {
        // 上传成功后后台立马进行校验，此时禁止导入新文件或关闭窗口，直到后台校验完成
        this.enableImportFile = false;
        // 禁用关闭按钮
        this.disableCloseBtn = true;
        // 隐藏原生的上传控件状态栏，显示自定义的校验中状态栏
        this.currentStatusBar = 'check';
        // 上传成功后需要调“查看批量配置执行进度”接口，根据后台返回的状态决定按钮启用禁用及页面展示
        this.updatingCheckStatus();
    }

    /**
     * 上传失败时的处理
     *
     * @memberof BatchConfigComponent
     */
    handleUploadErr(error): void {
        // 显示原生的上传控件状态栏，隐藏自定义的校验中状态栏
        this.currentStatusBar = 'upload';
        // 上传失败时后台不进行校验，此时允许用户导入新文件或关闭窗口
        this.enableImportFile = true;
        // 启用关闭按钮
        this.disableCloseBtn = false;
        // 按要求不显示错误码
        const { description, suggestion } = error;
        // 显示错误信息
        this.toast.error({ description, suggestion });
    }

    /**
     * 上传接口正常返回（包括成功和出错）回调函数
     *
     * @param {*} event
     * @memberof BatchConfigComponent
     */
    onUpload(event): void {
        // 与1.0保持一致，上传文件完成后清空上传框
        this.inputEL.uploadedFiles = [];
        // 兼容处理response
        let resp;
        const invalidRespError = {
            description: this.i18n.internalError,
            suggestion: this.i18n.contactEngineers,
        };
        try {
            resp = JSON.parse(event.xhr.response);
        } catch {
            // 此上传接口成功时返回空消息体，而不是返回 error.code 为0，因此做这样的兼容
            if ('' === event.xhr.response) {
                resp = {
                    error: {
                        code: ERROR_OK,
                    },
                };
            } else {
                resp = {
                    error: invalidRespError,
                };
            }
        }
        // 根据response做相应处理
        if (resp && resp.error) {
            if (resp.error.code === ERROR_OK) {
                this.handleUploadSuccess();
            } else {
                this.handleUploadErr(resp.error);
            }
        } else {
            this.handleUploadErr(invalidRespError);
        }

        const reader = new FileReader();
        if (reader) {
            reader.onload = (): void => {
                this.fileResult = reader.result;
            };

            // 读取上传的文件并转换为utf-8格式
            const file = event.file.file;
            reader.readAsText(file);
        }
    }

    /**
     * 上传接口非正常返回回调函数
     *
     * @param {*} event
     * @memberof BatchConfigComponent
     */
    onError(event): void {
        // 如果后台返回504，则显示超时字样
        if (event && event.xhr && 504 === event.xhr.status) {
            this.statusFailedLabel = this.i18n.importTimedOut;
        } else {
            this.statusFailedLabel = this.i18n.failed;
        }
        // 兼容处理response
        let resp;
        const invalidRespError = {
            description: this.i18n.commonError,
        };
        try {
            resp = JSON.parse(event.xhr.response);
        } catch {
            resp = {
                error: invalidRespError,
            };
        }
        // 根据response做相应处理
        if (resp && resp.error) {
            this.handleUploadErr(resp.error);
        } else {
            this.handleUploadErr(invalidRespError);
        }
    }

    /**
     * 延迟调用初始状态更新函数
     *
     * @param {number} [ms=0]
     * @memberof BatchConfigComponent
     */
    delayUpdatingInitStatus(ms = 5000): void {
        setTimeout(() => {
            this.updatingInitStatus();
        }, ms);
    }

    /**
     * 初始化页面及导入文件成功后，根据后台返回的状态决定按钮启用禁用及页面展示
     *
     * @memberof BatchConfigComponent
     */
    updatingInitStatus(): void {
        // 关闭页面时不再轮询
        if (!this.isBatchConfigShow) {
            return;
        }
        const successFn = (response): void => {
            if (!response) {
                return;
            }
            const { data } = response;
            if (!data) {
                return;
            }
            // 初始态。允许导入文件
            if (data.state === RunningStatue.initial) {
                this.notImported = true;
                this.enableImportFile = true;
                this.enableExcuteButton = false;
                this.disableCloseBtn = false;
                // 显示原生的上传控件状态栏，隐藏自定义的校验中状态栏
                this.currentStatusBar = 'upload';
            } else if (data.state === RunningStatue.loading) {
                this.notImported = true;
                this.enableImportFile = false;
                this.enableExcuteButton = false;
                // 隐藏原生的上传控件状态栏，显示自定义的校验中状态栏
                this.currentStatusBar = 'check';
                if (true === data.isUser) {
                    this.delayUpdatingInitStatus();
                    this.disableCloseBtn = true;
                } else {
                    if (false === data.isUser) {
                        this.showErrorPop(this.i18n.initCheckTitle, this.i18n.otherUser);
                    } else {
                        // 不知道是否是当前用户（不正常返回isUser）的情况
                        this.showErrorPop(this.i18n.initCheckTitle, this.i18n.abnormalStatus, true);
                    }
                    this.disableCloseBtn = false;
                    // 显示原生的上传控件状态栏，隐藏自定义的校验中状态栏
                    this.currentStatusBar = 'upload';
                }
            } else if (data.state === RunningStatue.checkFailed) {
                this.enableExcuteButton = false;
                this.disableCloseBtn = false;
                // 显示原生的上传控件状态栏，隐藏自定义的校验中状态栏
                this.currentStatusBar = 'upload';
                this.enableImportFile = true;
                // 非当前用户或不知道是否是当前用户（不正常返回isUser）的情况都不显示错误日志
                if (true === data.isUser) {
                    this.errorLineArr = data.result;
                    for (let i = 0; i < this.errorLineArr.length; i++) {
                        this.errorLineArr[i].command = this.utilService.decodeHTML(this.errorLineArr[i].command);
                    }
                } else {
                    this.notImported = true;
                }
            } else if (data.state === RunningStatue.notExecute) {
                this.disableCloseBtn = false;
                // 显示原生的上传控件状态栏，隐藏自定义的校验中状态栏
                this.currentStatusBar = 'upload';
                if (true === data.isUser) {
                    this.notImported = false;
                    this.enableImportFile = true;
                    this.enableExcuteButton = true;
                } else {
                    if (false === data.isUser) {
                        this.showErrorPop(this.i18n.initCheckTitle, this.i18n.otherUser);
                    } else {
                        // 不知道是否是当前用户（不正常返回isUser）的情况
                        this.showErrorPop(this.i18n.initCheckTitle, this.i18n.abnormalStatus, true);
                    }
                    this.notImported = true;
                    this.enableImportFile = false;
                    this.enableExcuteButton = false;
                }
            } else if (data.state === RunningStatue.executing || data.state === RunningStatue.stopInFailed) {
                this.enableContinueBtn = data.state === RunningStatue.stopInFailed;
                this.enableImportFile = false;
                this.enableExcuteButton = false;
                if (true === data.isUser) {
                    if (data.state === RunningStatue.stopInFailed) {
                        this.getState();
                    } else {
                        this.delayUpdatingInitStatus();
                        // 执行中的场景展示进度
                        this.cliProgress = data.completed;
                    }
                    // 显示自定义的执行中状态栏，隐藏原生的上传控件状态栏
                    this.currentStatusBar = 'executing';
                    this.disableCloseBtn = true;
                } else {
                    if (false === data.isUser) {
                        this.showErrorPop(this.i18n.initCheckTitle, this.i18n.otherUser);
                    } else {
                        // 不知道是否是当前用户（不正常返回isUser）的情况
                        this.showErrorPop(this.i18n.initCheckTitle, this.i18n.abnormalStatus, true);
                    }
                    // 显示原生的上传控件状态栏，隐藏自定义的校验中状态栏
                    this.currentStatusBar = 'upload';
                    this.disableCloseBtn = false;
                }
            }
        };

        const errorFn = (): void => {
            // 如果后台未返回任务状态，则交由后台决定用户是否可以上传文件
        };

        this.batchConfigService.getExecutionStatus().subscribe(successFn, errorFn);
    }

    /**
     * 弹框显示错误提示
     *
     * @param {*} title 提示标题
     * @param {*} msg 提示文本
     * @param {boolean} [forceQuit=false] 是否强制用户退出该模块
     * @memberof BatchConfigComponent
     */
    showErrorPop(title: string, msg: string, forceQuit = false): void {
        const config = {
            title,
            content: msg,
            onOk: (): void => {
                if (forceQuit) {
                    this.hideSlideWindow();
                } else {
                    this.clearConfigButton = true;
                }
                this.promptService.close();
            },
            onCancel: (): void => {
                if (forceQuit) {
                    this.hideSlideWindow();
                } else {
                    this.clearConfigButton = true;
                }
                this.promptService.close();
            },
        };
        this.promptService.fail(config);
    }

    /**
     * 延迟查询上传文件的校验结果
     *
     * @param {number} [ms=0]
     * @memberof BatchConfigComponent
     */
    delayUpdatingCheckStatus(ms = 5000): void {
        setTimeout(() => {
            this.updatingCheckStatus();
        }, ms);
    }


    /**
     * 上传完后查询上传文件的校验结果
     *
     * @memberof BatchConfigComponent
     */
    updatingCheckStatus(): void {
        // 关闭页面时不再轮询
        if (!this.isBatchConfigShow) {
            return;
        }
        const successFn = (response): void => {
            if (!response) {
                this.delayUpdatingCheckStatus();
                return;
            }
            const { data } = response;
            if (!data) {
                this.delayUpdatingCheckStatus();
                return;
            }
            // 初始态。允许导入文件
            if (data.state === RunningStatue.initial) {
                this.showErrorPop(this.i18n.fileCheckTitle, this.i18n.abnormalStatus);
                this.notImported = true;
                this.enableImportFile = true;
                this.enableExcuteButton = false;
                this.disableCloseBtn = false;
                // 显示原生的上传控件状态栏，隐藏自定义的校验中状态栏
                this.currentStatusBar = 'upload';
            } else if (data.state === RunningStatue.loading) {
                this.notImported = true;
                this.enableImportFile = false;
                this.enableExcuteButton = false;
                // 隐藏原生的上传控件状态栏，显示自定义的校验中状态栏
                this.currentStatusBar = 'check';
                if (true === data.isUser) {
                    this.delayUpdatingCheckStatus();
                    this.disableCloseBtn = true;
                } else {
                    if (false === data.isUser) {
                        this.showErrorPop(this.i18n.fileCheckTitle, this.i18n.otherUser);
                    } else {
                        // 不知道是否是当前用户（不正常返回isUser）的情况
                        this.showErrorPop(this.i18n.fileCheckTitle, this.i18n.abnormalStatus, true);
                    }
                    this.disableCloseBtn = false;
                }
            } else if (data.state === RunningStatue.checkFailed) {
                this.enableExcuteButton = false;
                this.disableCloseBtn = false;
                // 显示原生的上传控件状态栏，隐藏自定义的校验中状态栏
                this.currentStatusBar = 'upload';
                this.enableImportFile = true;
                if (true === data.isUser) {
                    this.errorLineArr = data.result;
                    for (let i = 0; i < this.errorLineArr.length; i++) {
                        this.errorLineArr[i].command = this.utilService.decodeHTML(this.errorLineArr[i].command);
                    }
                } else {
                    if (false === data.isUser) {
                        this.showErrorPop(this.i18n.fileCheckTitle, this.i18n.otherUser);
                    } else {
                        // 不知道是否是当前用户（不正常返回isUser）的情况
                        this.showErrorPop(this.i18n.fileCheckTitle, this.i18n.abnormalStatus, true);
                    }
                    this.notImported = true;
                }
            } else if (data.state === RunningStatue.notExecute) {
                this.disableCloseBtn = false;
                // 显示原生的上传控件状态栏，隐藏自定义的校验中状态栏
                this.currentStatusBar = 'upload';
                if (true === data.isUser) {
                    this.notImported = false;
                    this.enableImportFile = true;
                    this.enableExcuteButton = true;
                } else {
                    if (false === data.isUser) {
                        this.showErrorPop(this.i18n.fileCheckTitle, this.i18n.otherUser);
                    } else {
                        // 不知道是否是当前用户（不正常返回isUser）的情况
                        this.showErrorPop(this.i18n.fileCheckTitle, this.i18n.abnormalStatus, true);
                    }
                    this.notImported = true;
                    this.enableImportFile = false;
                    this.enableExcuteButton = false;
                }
            } else if (data.state === RunningStatue.executing || data.state === RunningStatue.stopInFailed) {
                this.enableContinueBtn = data.state === RunningStatue.stopInFailed;
                this.enableImportFile = false;
                this.enableExcuteButton = false;
                // 隐藏原生的上传控件状态栏，显示自定义的校验中状态栏
                this.currentStatusBar = 'check';
                if (true === data.isUser) {
                    this.showErrorPop(this.i18n.fileCheckTitle, this.i18n.abnormalStatus, true);
                    this.disableCloseBtn = true;
                } else {
                    if (false === data.isUser) {
                        this.showErrorPop(this.i18n.fileCheckTitle, this.i18n.otherUser);
                    } else {
                        // 不知道是否是当前用户（不正常返回isUser）的情况
                        this.showErrorPop(this.i18n.fileCheckTitle, this.i18n.abnormalStatus, true);
                    }
                    this.disableCloseBtn = false;
                }
            }
        };

        const errorFn = (): void => {
            this.delayUpdatingCheckStatus();
        };

        this.batchConfigService.getExecutionStatus().subscribe(successFn, errorFn);
    }

    /**
     * 暂停上传队列中文件的回调函数
     *
     * @param {*} event
     * @memberof BatchConfigComponent
     */
    onPaused(): void {
        // 启用关闭按钮
        this.disableCloseBtn = false;
        // 显示原生的上传控件状态栏，隐藏自定义的校验中状态栏
        this.currentStatusBar = 'upload';
    }

    /**
     * 取消排队上传队列中的文件的回调函数
     *
     * @param {*} event
     * @memberof BatchConfigComponent
     */
    onClear(): void {
        this.cliEchoArr = [];
        this.errorLineArr = [];
        // 隐藏执行完成图标及提示，显示原生的上传控件状态栏
        this.currentStatusBar = 'upload';
        this.notImported = true;
        this.enableExcuteButton = false;
    }

    /**
     * 清除已上传文件的回调函数
     *
     * @param {*} event
     * @memberof BatchConfigComponent
     */
    onHistoryClear(): void {
        this.cliEchoArr = [];
        this.errorLineArr = [];
        // 隐藏执行完成图标及提示，显示原生的上传控件状态栏
        this.currentStatusBar = 'upload';
        this.notImported = true;
        this.enableExcuteButton = false;
    }

    /**
     * 移除上传队列中文件的回调函数
     *
     * @param {*} event
     * @memberof BatchConfigComponent
     */
    onRemove(): void {
        this.cliEchoArr = [];
        this.errorLineArr = [];
        // 隐藏执行完成图标及提示，显示原生的上传控件状态栏
        this.currentStatusBar = 'upload';
        this.notImported = true;
        this.enableExcuteButton = false;
    }

    /**
     * 移除上传成功文件记录的回调函数
     *
     * @param {*} event
     * @memberof BatchConfigComponent
     */
    onHistoryRemove(): void {
        this.cliEchoArr = [];
        this.errorLineArr = [];
        // 隐藏执行完成图标及提示，显示原生的上传控件状态栏
        this.currentStatusBar = 'upload';
        this.notImported = true;
        this.enableExcuteButton = false;
    }

    /**
     * 上传控件选择完文件的回调函数
     *
     * @param {*} event
     * @memberof BatchConfigComponent
     */
    onSelect(): void {
        // 选取新文件后，禁用执行按钮
        this.enableExcuteButton = false;
        // 选取新文件后，置为未导入图标
        this.notImported = true;
    }

    /**
     * 上传控件前台校验报错回调
     *
     * @param {*} event
     * @memberof BatchConfigComponent
     */
    onValidateError(): void {
        // 上传文件校验不合法时，禁用执行按钮
        this.enableExcuteButton = false;
        this.notImported = true;
    }

    // 手动删除批量配置
    clearConfigExecute(): void {
        if (!this.clearConfigButton && !this.enableExcuteButton) {
            this.promptService.prompt({
                title: this.i18n.deleteByHandTitle,
                content: this.i18n.noProfile,
                width: 500,
                onOk: (): void => {
                    this.promptService.close();
                },
            });
            return;
        }
        this.promptService.promptConfirm({
            title: this.i18n.deleteByHandTitle,
            content: this.i18n.delete,
            width: 500,
            onOk: (): void => {
                this.promptService.close();
                this.batchConfigService.clearConfig().subscribe(
                    () => {
                        this.updatingInitStatus();
                    },
                    () => {
                        this.updatingInitStatus();
                    },
                );
            },
        });
    }

    /**
     * 做CLI批量配置前的检查
     *
     * @memberof BatchConfigComponent
     */
    checkBeforeExecute(): void {
        this.enableImportFile = false;
        const successFn = (response): void => {
            if (!response) {
                return;
            }
            const { data } = response;

            if (data && data.isUser === true) {
                this.doExcute();
            } else {
                this.showErrorPop(this.i18n.preExecutionCheckTitle, this.i18n.importError, true);
                this.cliEchoArr = [];
                this.enableExcuteButton = false;
                this.notImported = true;
                // 启用关闭按钮
                this.disableCloseBtn = false;
            }
        };

        const errorFn = (): void => {
            this.enableExcuteButton = true;
            this.enableImportFile = true;
        };

        this.batchConfigService.getExecutionStatus().subscribe(successFn, errorFn);
    }

    /**
     * 执行CLI批量配置
     *
     * @memberof BatchConfigComponent
     */
    doExcute(): void {
        // 禁用关闭按钮
        this.disableCloseBtn = true;
        const successFn = (response): void => {
            if (!response) {
                return;
            }
            const { result: { code } } = response;
            if (code === ERROR_OK) {
                // 重置CLI执行进度值
                this.cliProgress = 0;
                // 显示自定义的执行中状态栏，隐藏原生的上传控件状态栏
                this.currentStatusBar = 'executing';
                this.enableExcuteButton = false;
                this.enableImportFile = false;
                this.getState();
            }
        };

        const errorFn = (): void => {
            this.enableExcuteButton = true;
            this.enableImportFile = true;
            // 启用关闭按钮
            this.disableCloseBtn = false;
        };

        this.batchConfigService.executeCli().subscribe(successFn, errorFn);
    }

    /**
     * 轮询获取执行状态
     *
     * @memberof BatchConfigComponent
     */
    getState(): void {
        const successFn = (response): void => {
            if (!response) {
                return;
            }
            const { data } = response;
            if (!data) {
                return;
            }
            if (true === data.isUser) {
                let contentArr = data.result;
                let contentStr = '';
                if (Array.isArray(contentArr)) {
                    for (let i = 0; i < contentArr.length; i++) {
                        // 经过与ibase同事对齐，此接口他们只转义以下5个字符，所以可以调用该公共方法转回来
                        // m[‘<’]  = “&lt;”;
                        // m[‘>’]  = “&gt;”;
                        // m[‘(’]  = “&#40;”;
                        // m[‘)’]  = “&#41;”;
                        // m[‘&’]  = “&amp;”;
                        const decodedStr = this.utilService.decodeHTML(contentArr[i].cliecho);
                        contentStr += decodedStr;
                    }
                }
                contentArr = contentStr.split('\r\n');
                this.cliEchoArr = this.cliEchoArr.concat(contentArr);
                // 1.0老逻辑为：angular.element('#testdiv').html(contentStr.replaceAll('\r\n', '<br>'));
                this.cliProgress = data.completed;
                this.enableContinueBtn = false;

                switch (data.state) {
                    // 初始态。允许导入文件
                    case RunningStatue.initial:
                        // 暂停轮询
                        this.executionStatusPolling.pause();
                        // 启用关闭按钮
                        this.disableCloseBtn = false;
                        this.enableImportFile = true;
                        // 显示自定义的执行完成状态栏
                        this.currentStatusBar = 'executed';
                        break;
                    // 文件导入中
                    case RunningStatue.loading:
                    // 文件检查失败。允许导入文件
                    // eslint-disable-next-line no-fallthrough
                    case RunningStatue.checkFailed:
                    // 未执行
                    // eslint-disable-next-line no-fallthrough
                    case RunningStatue.notExecute:
                        // 此场景下 1,2,3 三种状态都为不应该出现的异常状态，做相同处理
                        this.executionStatusPolling.pause();
                        this.showErrorPop(this.i18n.executionCheckTitle, this.i18n.abnormalStatus, true);
                        break;
                    // 执行中
                    case RunningStatue.executing:
                        // do nothing, keep going
                        break;
                    // 未完成 【执行失败，可忽略后继续执行】
                    case RunningStatue.stopInFailed:
                        // 暂停轮询
                        this.executionStatusPolling.recover();
                        this.enableContinueBtn = true;
                        break;
                    default:
                        // do nothing, keep going
                        break;
                }
            } else {
                if (false === data.isUser) {
                    this.showErrorPop(this.i18n.executionCheckTitle, this.i18n.otherUser);
                } else {
                    // 不知道是否是当前用户（不正常返回isUser）的情况
                    this.showErrorPop(this.i18n.executionCheckTitle, this.i18n.abnormalStatus, true);
                }
                // 停止轮询
                this.batchConfigService.stopPolling(this.executionStatusPolling);
                // 启用关闭按钮
                this.disableCloseBtn = false;
            }
        };

        const errorFn = (): void => {
            // do nothing, keep going
        };

        if (!this.executionStatusPolling) {
            this.executionStatusPolling = this.batchConfigService.updatingExecutionStatus();
            this.executionStatusPolling.subscribe(successFn, errorFn);
        } else {
            this.executionStatusPolling.recover();
        }
    }

    /**
     * 停止执行CLI批量配置
     *
     * @memberof BatchConfigComponent
     */
    cliStop(): void {
        if (!this.enableStopButton) {
            return;
        }
        this.enableStopButton = false;
        const successFn = (): void => {
            // 显示原生的上传控件状态栏，隐藏自定义的执行中状态栏
            this.currentStatusBar = 'upload';
            this.enableStopButton = true;
            this.executionStatusPolling.recover();
        };

        const errorFn = (): void => {
            this.enableStopButton = true;
        };
        this.batchConfigService.stopExecuteCli().subscribe(successFn, errorFn);
    }

    /**
     * 继续执行CLI批量配置
     *
     * @memberof BatchConfigComponent
     */
    cliContinue(): void {
        if (!this.enableContinueBtn) {
            return;
        }
        this.enableContinueBtn = false;
        const successFn = (): void => {
            this.getState();
        };

        const errorFn = (): void => {
            this.enableContinueBtn = true;
        };
        this.batchConfigService.continueExecuteCli().subscribe(successFn, errorFn);
    }

    /*
     * 展示配置命令详情开关动作监听函数
     */
    showDetailCode(checked: boolean): void {
        this.isShowDetailCode = checked;
    }
}
